import { useState, useEffect, useRef } from 'react';
import { aiAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Bot,
  Send,
  User,
  Sparkles,
  Wallet,
  Users,
  FileCheck,
  Loader2,
  MessageSquare,
  Lightbulb,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const agentConfig = {
  chef_exploitation: {
    name: 'Chef d\'Exploitation',
    icon: Sparkles,
    color: 'bg-primary text-primary-foreground',
    description: 'Vision globale, alertes stratégiques, priorités',
  },
  financier: {
    name: 'Financier',
    icon: Wallet,
    color: 'bg-amber-500 text-white',
    description: 'Trésorerie, échéances, simulations',
  },
  commercial: {
    name: 'Commercial',
    icon: Users,
    color: 'bg-blue-500 text-white',
    description: 'Clients, prospects, pipeline',
  },
  reglementaire: {
    name: 'Réglementaire',
    icon: FileCheck,
    color: 'bg-purple-500 text-white',
    description: 'PAC, IFT, conformité, aides',
  },
};

const MessageBubble = ({ message, isUser }) => {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex items-start gap-3 max-w-[80%] ${isUser ? 'flex-row-reverse' : ''}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-primary' : 'bg-muted'
        }`}>
          {isUser ? (
            <User className="w-4 h-4 text-primary-foreground" />
          ) : (
            <Bot className="w-4 h-4 text-foreground" />
          )}
        </div>
        <div className={`p-4 rounded-2xl ${
          isUser 
            ? 'bg-primary text-primary-foreground rounded-br-md' 
            : 'bg-muted text-foreground rounded-bl-md'
        }`}>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
        </div>
      </div>
    </div>
  );
};

const SuggestionChip = ({ text, onClick }) => (
  <button
    onClick={onClick}
    className="px-3 py-2 text-sm bg-muted hover:bg-muted/80 rounded-full transition-colors text-left"
    data-testid={`suggestion-${text.substring(0, 20)}`}
  >
    {text}
  </button>
);

const AgentCard = ({ agentType, isActive, onClick }) => {
  const config = agentConfig[agentType];
  const Icon = config.icon;

  return (
    <Card 
      className={`cursor-pointer transition-all ${
        isActive ? 'ring-2 ring-primary' : 'hover:shadow-md'
      }`}
      onClick={onClick}
      data-testid={`agent-card-${agentType}`}
    >
      <CardContent className="pt-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm">{config.name}</h3>
            <p className="text-xs text-muted-foreground mt-1">{config.description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const IAPage = () => {
  const [selectedAgent, setSelectedAgent] = useState('chef_exploitation');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const res = await aiAPI.getSuggestions(selectedAgent);
        setSuggestions(res.data.suggestions || []);
      } catch (error) {
        console.error(error);
      }
    };

    fetchSuggestions();
    // Reset conversation when agent changes
    setMessages([]);
    setConversationId(null);
  }, [selectedAgent]);

  const handleSend = async (text = input) => {
    if (!text.trim() || loading) return;

    const userMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await aiAPI.chat({
        message: text,
        agent_type: selectedAgent,
        conversation_id: conversationId,
      });

      const assistantMessage = { role: 'assistant', content: res.data.response };
      setMessages((prev) => [...prev, assistantMessage]);
      setConversationId(res.data.conversation_id);
      
      if (res.data.suggestions) {
        setSuggestions(res.data.suggestions);
      }
    } catch (error) {
      toast.error('Erreur lors de la communication avec l\'IA');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setConversationId(null);
  };

  const config = agentConfig[selectedAgent];
  const Icon = config.icon;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ia-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Assistant IA</h1>
          <p className="text-muted-foreground mt-1">
            Vos agents IA métiers - ils proposent, vous décidez
          </p>
        </div>
      </div>

      {/* Agent selector */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.keys(agentConfig).map((agentType) => (
          <AgentCard
            key={agentType}
            agentType={agentType}
            isActive={selectedAgent === agentType}
            onClick={() => setSelectedAgent(agentType)}
          />
        ))}
      </div>

      {/* Chat interface */}
      <Card className="overflow-hidden" data-testid="chat-container">
        <CardHeader className={`${config.color} py-3`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon className="w-5 h-5" />
              <div>
                <CardTitle className="text-base">Agent {config.name}</CardTitle>
                <p className="text-xs opacity-80">{config.description}</p>
              </div>
            </div>
            {messages.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetConversation}
                className="text-inherit hover:bg-white/20"
                data-testid="reset-conversation-button"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Nouvelle conversation
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Messages area */}
          <ScrollArea className="h-[400px] p-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-8">
                <div className={`w-16 h-16 rounded-2xl ${config.color} flex items-center justify-center mb-4`}>
                  <Icon className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  Bonjour, je suis votre Agent {config.name}
                </h3>
                <p className="text-muted-foreground text-sm max-w-md mb-6">
                  Je suis là pour vous aider. Posez-moi une question ou choisissez une suggestion ci-dessous.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestions.slice(0, 4).map((suggestion, index) => (
                    <SuggestionChip
                      key={index}
                      text={suggestion}
                      onClick={() => handleSend(suggestion)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((message, index) => (
                  <MessageBubble
                    key={index}
                    message={message}
                    isUser={message.role === 'user'}
                  />
                ))}
                {loading && (
                  <div className="flex justify-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </ScrollArea>

          {/* Suggestions (when there are messages) */}
          {messages.length > 0 && suggestions.length > 0 && (
            <div className="px-4 py-2 border-t border-border">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-muted-foreground">Suggestions</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestions.slice(0, 3).map((suggestion, index) => (
                  <SuggestionChip
                    key={index}
                    text={suggestion}
                    onClick={() => handleSend(suggestion)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="p-4 border-t border-border">
            <div className="flex gap-3">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Posez votre question à l'Agent ${config.name}...`}
                disabled={loading}
                className="flex-1"
                data-testid="chat-input"
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="rounded-full px-4"
                data-testid="send-message-button"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              L'IA propose des suggestions, c'est vous qui décidez.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IAPage;
